/**
 * Membership Filter Toggle System
 * 
 * Handles operator/non-operator filtering of membership cards
 * with smooth transitions and localStorage persistence
 *
 * @package bricks-arival
 */

(function() {
	'use strict';

	/**
	 * Membership Filter Object
	 */
	const MembershipFilter = {
		// Configuration
		config: {
			storageKey: 'arival_membership_filter_selection',
			storageTimestampKey: 'arival_membership_filter_timestamp',
			animationDuration: 650, // Longer for smoother, more effortless feel
			cardSelector: '[data-operator-type]',
			buttonSelector: '[data-filter-button]',
			containerSelector: '.membership-filter-container',
			hiddenClass: 'membership-filter-hidden',
			visibleClass: 'membership-filter-visible',
			placeholderClass: 'membership-filter-placeholder',
			activeButtonClass: 'membership-filter-active',
			expirationDays: 30 // Default, will be overridden by localized data
		},

		// State
		currentFilter: null,
		userOperatorType: null,
		isInitialized: false,

		/**
		 * Initialize the filter system
		 * Simplified approach - try immediately, on DOMContentLoaded, and with MutationObserver fallback
		 */
		init: function() {
			if (this.isInitialized) {
				return;
			}

			// Get localized data if available
			if (typeof arivalMembershipFilter !== 'undefined') {
				this.userOperatorType = arivalMembershipFilter.userOperatorType || null;
				this.config.storageKey = arivalMembershipFilter.storageKey || this.config.storageKey;
				this.config.expirationDays = arivalMembershipFilter.expirationDays || this.config.expirationDays;
			}

			console.log('[MembershipFilter] Init called');

			// Try to setup immediately
			this.attemptSetup();

			// Setup based on document ready state
			if (document.readyState === 'loading') {
				// DOM still loading - wait for DOMContentLoaded
				document.addEventListener('DOMContentLoaded', () => {
					console.log('[MembershipFilter] DOMContentLoaded');
					this.attemptSetup();
				});
			} else {
				// DOM already ready - try with small delay for Bricks dynamic content
				setTimeout(() => this.attemptSetup(), 100);
			}

			// Add MutationObserver as fallback for dynamic content (Bricks Builder)
			this.setupMutationObserver();
		},

		/**
		 * Setup MutationObserver to detect dynamically added content
		 * Only used as fallback for Bricks dynamic content
		 */
		setupMutationObserver: function() {
			if (!window.MutationObserver || this.isInitialized) {
				return;
			}

			const observer = new MutationObserver(() => {
				if (!this.isInitialized) {
					this.attemptSetup();
				} else {
					observer.disconnect();
				}
			});

			// Start observing when body is available
			const startObserving = () => {
				if (document.body) {
					observer.observe(document.body, {
						childList: true,
						subtree: true
					});

					// Auto-disconnect after 5 seconds or when initialized
					setTimeout(() => observer.disconnect(), 5000);
				}
			};

			if (document.body) {
				startObserving();
			} else {
				document.addEventListener('DOMContentLoaded', startObserving);
			}
		},

		/**
		 * Attempt to setup the filter system
		 * Will only run if not already initialized and elements exist
		 */
		attemptSetup: function() {
			if (this.isInitialized) {
				return;
			}

			const cards = document.querySelectorAll(this.config.cardSelector);
			const buttons = document.querySelectorAll(this.config.buttonSelector);
			const dropdowns = document.querySelectorAll('.company-type-dropdown.select2-select');

			// Only setup if we have cards AND (buttons OR dropdown)
			// This prevents JS from applying to cards when there are no controls
			if (cards.length > 0 && (buttons.length > 0 || dropdowns.length > 0)) {
				this.setup();
			}
		},

		/**
		 * Setup filter system
		 */
		setup: function() {
			const cards = document.querySelectorAll(this.config.cardSelector);
			const buttons = document.querySelectorAll(this.config.buttonSelector);

			// Exit if no cards found (buttons are optional)
			if (cards.length === 0) {
				return;
			}

			console.log('[MembershipFilter] Initializing with', cards.length, 'cards and', buttons.length, 'buttons (or dropdown)');

			// Determine initial filter state FIRST
			this.determineInitialFilter();

			console.log('[MembershipFilter] Current filter:', this.currentFilter);

			// Update button states immediately based on stored preference (if buttons exist)
			if (buttons.length > 0) {
				this.updateButtonStates(this.currentFilter);
			}

			// Cards are already in placeholder state via CSS
			// Mark them with placeholder class for JavaScript control
			cards.forEach(card => {
				card.classList.add(this.config.placeholderClass);
			});

			// Setup button event listeners (if buttons exist)
			if (buttons.length > 0) {
				this.setupButtons();
			}

			// Apply initial filter - transition from placeholder to filtered
			if (this.currentFilter) {
				console.log('[MembershipFilter] Applying filter:', this.currentFilter);
				// Transition from placeholder to filtered cards with animation
				this.applyFilter(this.currentFilter, true);
			} else {
				console.log('[MembershipFilter] No filter - staying in placeholder state');
				// Already in placeholder state, just ensure classes are set
				cards.forEach(card => {
					card.classList.add(this.config.placeholderClass);
					card.classList.add(this.config.hiddenClass);
					card.classList.remove(this.config.visibleClass);
				});
			}

			this.isInitialized = true;
			console.log('[MembershipFilter] Initialization complete');
			
			// Dispatch custom event to notify other systems that filter is ready
			if (typeof window.CustomEvent !== 'undefined') {
				const initEvent = new CustomEvent('arivalMembershipFilterReady', {
					detail: {
						currentFilter: this.currentFilter,
						filterSystem: this
					}
				});
				document.dispatchEvent(initEvent);
			}
		},

		/**
		 * Determine initial filter state
		 * Priority: 1. Logged-in user type, 2. localStorage, 3. null (show nothing)
		 */
		determineInitialFilter: function() {
			// First priority: logged-in user's operator type
			if (this.userOperatorType) {
				this.currentFilter = this.userOperatorType;
				return;
			}

			// Second priority: localStorage
			const savedFilter = this.getStoredFilter();
			if (savedFilter) {
				this.currentFilter = savedFilter;
				return;
			}

			// Default: no filter (cards stay hidden)
			this.currentFilter = null;
		},

		/**
		 * Hide all membership cards
		 * Shows placeholder state instead
		 */
		hideAllCards: function() {
			const cards = document.querySelectorAll(this.config.cardSelector);
			cards.forEach(card => {
				card.classList.add(this.config.hiddenClass);
				card.classList.remove(this.config.visibleClass);
				card.classList.add(this.config.placeholderClass);
				// Keep display so placeholder styling shows
				card.style.display = '';
				card.style.opacity = '0.3';
			});
		},

		/**
		 * Setup button event listeners
		 */
		setupButtons: function() {
			const buttons = document.querySelectorAll(this.config.buttonSelector);
			
			buttons.forEach(button => {
				button.addEventListener('click', (e) => {
					e.preventDefault();
					const filterValue = button.getAttribute('data-filter-value');
					
					if (filterValue) {
						// Check if this button is already active (toggle off)
						if (this.currentFilter === filterValue) {
							this.handleFilterDeactivate();
						} else {
							this.handleFilterChange(filterValue);
						}
					}
				});
			});
		},

		/**
		 * Handle filter change
		 * 
		 * @param {string} filterValue 'operator' or 'non-operator'
		 */
		handleFilterChange: function(filterValue) {
			// Update current filter
			this.currentFilter = filterValue;

			// Save to localStorage
			this.saveFilter(filterValue);

			// Update button states
			this.updateButtonStates(filterValue);

			// Apply filter with animation (always animate for smooth transitions)
			this.applyFilter(filterValue, true);
		},

		/**
		 * Handle filter deactivation (toggle off)
		 */
		handleFilterDeactivate: function() {
			// Clear current filter
			this.currentFilter = null;

			// Clear localStorage
			this.clearStoredFilter();

			// Update button states (no active buttons)
			this.updateButtonStates(null);

			// Show placeholder state
			this.showPlaceholderState();
		},

		/**
		 * Apply filter to cards
		 * Always animates cards out first, then shows appropriate ones
		 * 
		 * @param {string} filterValue 'operator' or 'non-operator'
		 * @param {boolean} animate Whether to animate the transition
		 */
		applyFilter: function(filterValue, animate = true) {
			const cards = document.querySelectorAll(this.config.cardSelector);
			
			if (cards.length === 0) {
				return;
			}

			// First, animate all cards out (remove placeholder if present)
			cards.forEach(card => {
				// Remove placeholder class first so transitions work properly
				const wasPlaceholder = card.classList.contains(this.config.placeholderClass);
				card.classList.remove(this.config.placeholderClass);
				
				// If transitioning from placeholder, ensure smooth transition
				if (wasPlaceholder && animate) {
					// Clear any inline styles to let CSS handle transition
					card.style.opacity = '';
					card.style.filter = '';
				}
				
				this.hideCard(card, animate);
			});

			// Then, after animation delay, show appropriate cards
			if (animate) {
				setTimeout(() => {
					cards.forEach(card => {
						const cardType = card.getAttribute('data-operator-type');
						const shouldShow = this.shouldShowCard(cardType, filterValue);

						if (shouldShow) {
							this.showCard(card, true);
						}
					});
				}, this.config.animationDuration);
			} else {
				// Immediate show if no animation
				cards.forEach(card => {
					const cardType = card.getAttribute('data-operator-type');
					const shouldShow = this.shouldShowCard(cardType, filterValue);

					if (shouldShow) {
						this.showCard(card, false);
					}
				});
			}
		},

		/**
		 * Show placeholder state for all cards
		 * 
		 * @param {boolean} animate Whether to animate transition
		 */
		showPlaceholderState: function(animate = true) {
			const cards = document.querySelectorAll(this.config.cardSelector);
			
			if (cards.length === 0) {
				return;
			}

			if (animate) {
				// Animate all cards out first
				cards.forEach(card => {
					this.hideCard(card, true);
				});

				// Then fade in placeholder state after animation
				setTimeout(() => {
					cards.forEach(card => {
						// Remove inline styles to let CSS transitions work
						card.style.opacity = '';
						card.style.transform = '';
						card.style.filter = '';
						
						card.classList.add(this.config.placeholderClass);
						card.classList.add(this.config.hiddenClass);
						card.classList.remove(this.config.visibleClass);
						card.style.display = '';
						
						// Add entering class for transition
						card.classList.add('membership-filter-placeholder-entering');
						card.offsetHeight; // Trigger reflow
						
						// Remove entering class to trigger fade-in transition
						requestAnimationFrame(() => {
							card.classList.remove('membership-filter-placeholder-entering');
						});
					});
				}, this.config.animationDuration);
			} else {
				// Immediate placeholder state (for initial load)
				cards.forEach(card => {
					// Remove inline styles
					card.style.opacity = '';
					card.style.transform = '';
					card.style.filter = '';
					
					card.classList.add(this.config.placeholderClass);
					card.classList.add(this.config.hiddenClass);
					card.classList.remove(this.config.visibleClass);
					card.style.display = '';
				});
			}
		},

		/**
		 * Determine if a card should be shown
		 * 
		 * @param {string} cardType Card's data-operator-type value
		 * @param {string} filterValue Current filter value
		 * @return {boolean}
		 */
		shouldShowCard: function(cardType, filterValue) {
			if (!cardType || !filterValue) {
				return false;
			}

			// Normalize values
			cardType = cardType.toLowerCase().trim();
			filterValue = filterValue.toLowerCase().trim();

			// 'both' shows for any filter
			if (cardType === 'both') {
				return true;
			}

			// Exact match
			if (cardType === filterValue) {
				return true;
			}

			return false;
		},

		/**
		 * Show a card with smooth, effortless animation
		 * Double RAF for buttery smooth transitions
		 * 
		 * @param {HTMLElement} card Card element
		 * @param {boolean} animate Whether to animate
		 */
		showCard: function(card, animate = true) {
			// Remove placeholder and hidden classes
			card.classList.remove(this.config.placeholderClass);
			card.classList.remove(this.config.hiddenClass);
			card.classList.add(this.config.visibleClass);

			// Show the card
			card.style.display = '';

			if (animate) {
				// Start from slightly below and transparent
				card.style.opacity = '0';
				card.style.transform = 'translateY(20px) scale(0.96)';
				
				// Double RAF for buttery smooth animation start
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						card.style.opacity = '1';
						card.style.transform = 'translateY(0) scale(1)';
					});
				});
			} else {
				// Immediate show (no animation)
				card.style.opacity = '1';
				card.style.transform = 'translateY(0) scale(1)';
			}
		},

		/**
		 * Hide a card with smooth, effortless animation
		 * Flows naturally with subtle upward movement
		 * 
		 * @param {HTMLElement} card Card element
		 * @param {boolean} animate Whether to animate
		 */
		hideCard: function(card, animate = true) {
			// Remove visible class
			card.classList.remove(this.config.visibleClass);
			card.classList.add(this.config.hiddenClass);

			if (animate) {
				// Start smooth fade out with upward movement
				requestAnimationFrame(() => {
					card.style.opacity = '0';
					card.style.transform = 'translateY(-20px) scale(0.96)';
				});
				
				// Hide after animation completes with small buffer
				setTimeout(() => {
					if (card.classList.contains(this.config.hiddenClass)) {
						// Only hide display if not placeholder
						if (!card.classList.contains(this.config.placeholderClass)) {
							card.style.display = 'none';
						}
					}
				}, this.config.animationDuration + 50);
			} else {
				// Immediate hide (no animation)
				card.style.opacity = '0';
				card.style.transform = 'translateY(-20px) scale(0.96)';
				if (!card.classList.contains(this.config.placeholderClass)) {
					card.style.display = 'none';
				}
			}
		},

		/**
		 * Update button active states
		 * 
		 * @param {string} activeFilter Currently active filter value (or null)
		 */
		updateButtonStates: function(activeFilter) {
			const buttons = document.querySelectorAll(this.config.buttonSelector);
			
			// Exit if no buttons found (dropdown-only scenario)
			if (buttons.length === 0) {
				return;
			}
			
			buttons.forEach(button => {
				const buttonValue = button.getAttribute('data-filter-value');
				
				if (activeFilter && buttonValue === activeFilter) {
					button.classList.add(this.config.activeButtonClass);
					button.setAttribute('aria-pressed', 'true');
				} else {
					button.classList.remove(this.config.activeButtonClass);
					button.setAttribute('aria-pressed', 'false');
				}
			});
		},

		/**
		 * Set a cookie
		 * @param {string} name Cookie name
		 * @param {string} value Cookie value
		 * @param {number} days Expiry in days (negative to delete)
		 */
		setCookie: function(name, value, days) {
			const date = new Date();
			date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
			const expires = 'expires=' + date.toUTCString();
			const secure = location.protocol === 'https:' ? ';Secure' : '';
			document.cookie = name + '=' + encodeURIComponent(value) + ';' + expires + ';path=/;SameSite=Lax' + secure;
		},

		/**
		 * Save filter to localStorage with timestamp
		 * 
		 * @param {string} filterValue Filter value to save
		 */
		saveFilter: function(filterValue) {
			try {
				const timestamp = Date.now();
				localStorage.setItem(this.config.storageKey, filterValue);
				localStorage.setItem(this.config.storageTimestampKey, timestamp.toString());
			} catch (e) {
				// localStorage not available or quota exceeded
				console.warn('Membership filter: Could not save to localStorage', e);
			}
			// Also set cookie for server-side PHP access at checkout
			const cookieValue = (filterValue === 'non-operator') ? 'non-op' : 'operator';
			this.setCookie('arival_operator_type', cookieValue, this.config.expirationDays);
		},

		/**
		 * Get stored filter from localStorage
		 * Checks expiration and returns null if expired
		 * 
		 * @return {string|null}
		 */
		getStoredFilter: function() {
			try {
				const filterValue = localStorage.getItem(this.config.storageKey);
				const timestamp = localStorage.getItem(this.config.storageTimestampKey);

				// If no value or timestamp, return null
				if (!filterValue || !timestamp) {
					return null;
				}

				// Check if expired
				const savedTime = parseInt(timestamp, 10);
				const currentTime = Date.now();
				const expirationMs = this.config.expirationDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds
				const timeDiff = currentTime - savedTime;

				if (timeDiff > expirationMs) {
					// Expired - clear storage and return null
					this.clearStoredFilter();
					return null;
				}

				return filterValue;
			} catch (e) {
				// localStorage not available
				return null;
			}
		},

		/**
		 * Clear stored filter and timestamp
		 */
		clearStoredFilter: function() {
			try {
				localStorage.removeItem(this.config.storageKey);
				localStorage.removeItem(this.config.storageTimestampKey);
			} catch (e) {
				console.warn('Membership filter: Could not clear localStorage', e);
			}
			// Also clear the operator type cookie
			this.setCookie('arival_operator_type', '', -1);
		},

		/**
		 * Reset filter (public method)
		 */
		reset: function() {
			this.clearStoredFilter();
			this.currentFilter = null;
			this.hideAllCards();
			this.updateButtonStates(null);
		}
	};

	// Initialize - handles all setup internally
	MembershipFilter.init();

	// Expose to global scope for manual control if needed
	window.ArivalMembershipFilter = MembershipFilter;

})();

